import React, { useEffect } from 'react';
import { Container } from 'react-bootstrap';
import { motion, useAnimation, useReducedMotion } from 'framer-motion';
import { Code, Link2, RefreshCw, Lightbulb } from 'lucide-react';
import logo from '../../assets/og_logo.webp';
import ScrollReveal from '../ScrollReveal';

const services = [
    { icon: Code, label: 'Web and Mobile Apps' },
    { icon: Link2, label: 'System Integrations' },
    { icon: RefreshCw, label: 'Legacy Modernisation' },
    { icon: Lightbulb, label: 'IT Consultancy' },
];

const stripVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.16 } },
};

const cardVariants = {
    hidden: { opacity: 0, y: 24 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: 'easeOut' } },
};

const tagline = 'Smart solutions for all your business needs, OGS guaranteed.';

const taglineStagger = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.16, delayChildren: 0.4 } },
};

const taglineWord = {
    hidden: { opacity: 0, y: 8 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: 'easeOut' } },
};

function Home() {
    const shouldReduceMotion = useReducedMotion();
    const logoControls = useAnimation();

    useEffect(() => {
        if (shouldReduceMotion) {
            logoControls.set({ opacity: 1, scale: 1 });
            return;
        }

        logoControls
            .start({ opacity: 1, scale: 1, transition: { duration: 0.85, ease: 'easeOut' } })
            .then(() => {
                logoControls.start({
                    scale: [1, 1.03, 1],
                    transition: { duration: 4, repeat: Infinity, ease: 'easeInOut' },
                });
            });
    }, [shouldReduceMotion, logoControls]);

    return (
        <motion.div
            initial={shouldReduceMotion ? false : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={shouldReduceMotion ? {} : { opacity: 0, y: -10 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
        >
            <Container fluid className="limited-width bg ogs-pri-text text-center py-5 mb-5">
                <motion.img
                    src={logo}
                    alt="OGS logo"
                    className="ogs-hero-logo"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={logoControls}
                />
                <motion.h1
                    className="ogs-sec-text mb-3 ogs-hero-tagline"
                    variants={shouldReduceMotion ? {} : taglineStagger}
                    initial="hidden"
                    animate="visible"
                >
                    {tagline.split(' ').map((word, i) => (
                        <motion.span
                            key={`${word}-${i}`}
                            className="ogs-hero-tagline-word"
                            variants={shouldReduceMotion ? {} : taglineWord}
                        >
                            {word}
                        </motion.span>
                    ))}
                </motion.h1>
            </Container>

            <ScrollReveal delay={0.2}>
                <Container fluid className="limited-width bg ogs-pri-text text-center py-5 mb-5">
                    <p className="ogs-pri-text mb-4">
                        At <b className="ogs-high-text">Omni Group Services (OGS)</b>, we help businesses get things done – from robust software systems to practical support that keeps operations moving.
                        With more than 10 years of hands-on development experience, technology is our core, but we don’t stop there.
                        <br /><br />
                        Need a reliable web or mobile app, integrations that actually talk to each other, or help modernising an old system? We can design, build, and support it.
                        Looking for a partner who understands both IT and day-to-day business needs? That’s where we fit in.
                        <br /><br />
                        Think of OGS as your versatile problem-solving partner: focused on quality, easy to work with, and serious about delivering results – with a bit of personality along the way.
                    </p>
                    <div className="ogs-image-overlay">
                        <img
                            src="https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=1200&q=80"
                            alt="Modern tech team working at laptops in a dark office"
                        />
                    </div>
                </Container>
            </ScrollReveal>

            <Container fluid className="limited-width bg ogs-pri-text py-5 mb-5">
                <motion.div
                    variants={shouldReduceMotion ? {} : stripVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: '-80px' }}
                >
                    <div className="ogs-service-grid">
                        {services.map(({ icon: Icon, label }) => (
                            <motion.div
                                key={label}
                                className="ogs-service-card"
                                variants={shouldReduceMotion ? {} : cardVariants}
                                whileHover={{ y: -4, boxShadow: '0 8px 30px rgba(111, 31, 209, 0.35)' }}
                                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                            >
                                <Icon className="ogs-service-card-icon" />
                                <div className="ogs-service-card-label">{label}</div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>
            </Container>
        </motion.div>
    );
}

export default Home;
